/**
 * Redeeming an impersonation handoff at boot.
 *
 * An operator who clicks "Connect as" in the admin portal arrives here with an
 * opaque handoff in the URL fragment. This trades it for a real session —
 * `redeem_impersonation` performs an Auth0 Custom Token Exchange server-side and
 * returns the tokens — which then drives an {@link ImpersonationClient} in place
 * of the Auth0 SPA client.
 *
 * This runs BEFORE the router and before anything checks for a session, because
 * that is the only point where there is no session to conflict with. It is not a
 * route for the same reason: every route in this app hangs off the authenticated
 * shell, and a pre-auth exception carved into that tree would be a permanent
 * invitation to widen.
 *
 * The fragment is consumed the moment it is read — stripped from the address bar
 * before the network call — so a reload cannot replay it, the operator cannot
 * paste it out of the URL bar by accident, and it never appears in a screenshot
 * of the window. It was never sent to a server on the way in either; fragments
 * are not transmitted, logged, or attached to `Referer`.
 */
import type { ImpersonationSessionTokens } from "@authe/fe-commons/session";

/** Fragment parameter the admin portal writes. */
export const HANDOFF_PARAM = "handoff";

export interface HandoffRedemption {
  tokens: ImpersonationSessionTokens;
  impersonationId: string;
  /** ISO-8601 — when the session dies. The authorizer enforces it too. */
  expiresAt: string | null;
  accessLevel: "read_only" | "read_write";
}

/** The two bits of browser this needs, injectable so the seam is testable. */
export interface HandoffLocation {
  hash: string;
  pathname: string;
  search: string;
}

export interface HandoffHistory {
  replaceState(data: unknown, unused: string, url: string): void;
}

/**
 * Take the handoff out of the URL, if there is one.
 *
 * Always removes it, even when what follows fails: a handoff is single-use, so
 * one left in the address bar is at best a dead link and at worst something an
 * operator pastes into a ticket.
 */
export function consumeHandoff(
  location: HandoffLocation = window.location,
  history: HandoffHistory = window.history,
): string | null {
  const hash = location.hash.startsWith("#") ? location.hash.slice(1) : location.hash;
  if (!hash) return null;

  const params = new URLSearchParams(hash);
  const handoff = params.get(HANDOFF_PARAM);
  if (!handoff) return null;

  params.delete(HANDOFF_PARAM);
  const remaining = params.toString();
  history.replaceState(
    null,
    "",
    `${location.pathname}${location.search}${remaining ? `#${remaining}` : ""}`,
  );
  return handoff;
}

export class HandoffRedemptionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "HandoffRedemptionError";
  }
}

/**
 * Trade the handoff for a session.
 *
 * Uses `fetch` rather than the shared `ApiClient` on purpose: that client exists
 * to attach a bearer token and this is the one call in the app made by someone
 * who has none. Wiring it through would mean teaching it about a request that
 * must NOT be authenticated, which is a strange thing to teach an API client.
 */
export async function redeemHandoff(
  handoff: string,
  options: { apiBaseDomain: string; fetchImpl?: typeof fetch; continent?: string },
): Promise<HandoffRedemption> {
  const continent = options.continent ?? "ANY";
  const url = `https://api.${options.apiBaseDomain}/authentication/POST/${continent}/v0/redeem-impersonation`;
  const doFetch = options.fetchImpl ?? fetch;

  let response: Response;
  try {
    response = await doFetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Source-Platform": "web",
        "Source-Region": continent,
        "Destination-Region": continent,
      },
      body: JSON.stringify({ handoff }),
    });
  } catch (cause) {
    throw new HandoffRedemptionError("Could not reach the authentication service.");
  }

  const body = (await response.json().catch(() => null)) as
    | { ok?: boolean; result?: Record<string, unknown>; error?: { message?: string } }
    | null;

  // The envelope carries the real outcome: a refused handoff is `ok: false` with
  // a 4xx `statusCode` inside a 200 response.
  if (!response.ok || !body?.ok || !body.result) {
    throw new HandoffRedemptionError(
      body?.error?.message ?? "This impersonation handoff is no longer valid.",
    );
  }

  const result = body.result;
  const accessToken = typeof result.access_token === "string" ? result.access_token : "";
  const idToken = typeof result.id_token === "string" ? result.id_token : "";
  if (!accessToken || !idToken) {
    // A session missing half its tokens would boot into a confusing half-state;
    // refusing keeps the failure at the point it happened.
    throw new HandoffRedemptionError("The impersonation session came back incomplete.");
  }

  return {
    tokens: {
      access_token: accessToken,
      id_token: idToken,
      expires_in: typeof result.expires_in === "number" ? result.expires_in : undefined,
    },
    impersonationId: String(result.impersonation_id ?? ""),
    expiresAt: typeof result.expires_at === "string" ? result.expires_at : null,
    accessLevel: result.access_level === "read_write" ? "read_write" : "read_only",
  };
}
